import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useBusinessContext } from "@/providers/business-provider";
import { useToast } from "@/hooks/use-toast";
import { 
  Download, 
  Eye, 
  Save, 
  Settings, 
  BarChart3, 
  PieChart, 
  LineChart,
  Table,
  Filter,
  Sparkles,
  TrendingUp,
  DollarSign
} from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";

interface ReportConfig {
  id?: string;
  name: string;
  type: 'profit-loss' | 'balance-sheet' | 'cash-flow' | 'custom';
  dateRange: {
    start: Date;
    end: Date;
  };
  filters: {
    accounts?: string[];
    categories?: string[];
    departments?: string[];
  };
  visualization: 'table' | 'bar-chart' | 'pie-chart' | 'line-chart';
  groupBy?: 'month' | 'quarter' | 'year' | 'category';
  includeSubAccounts: boolean;
  compareWithPrevious: boolean;
}

const REPORT_TYPES = [
  { value: 'profit-loss', label: 'Profit & Loss Statement', icon: TrendingUp },
  { value: 'balance-sheet', label: 'Balance Sheet', icon: DollarSign },
  { value: 'cash-flow', label: 'Cash Flow Statement', icon: LineChart },
  { value: 'custom', label: 'Custom Analytics Report', icon: Settings }
];

const VISUALIZATION_TYPES = [
  { value: 'table', label: 'Professional Table', icon: Table },
  { value: 'bar-chart', label: 'Bar Chart Analysis', icon: BarChart3 },
  { value: 'pie-chart', label: 'Pie Chart Breakdown', icon: PieChart },
  { value: 'line-chart', label: 'Trend Line Chart', icon: LineChart }
];

export function ReportBuilder() {
  const { activeBusiness } = useBusinessContext();
  const { toast } = useToast();
  const [config, setConfig] = useState<ReportConfig>({
    name: '',
    type: 'profit-loss',
    dateRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      end: new Date()
    },
    filters: {},
    visualization: 'table',
    includeSubAccounts: true,
    compareWithPrevious: false
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [savedReports, setSavedReports] = useState<ReportConfig[]>([]);

  useEffect(() => {
    if (activeBusiness?.id) {
      loadSavedReports();
    }
  }, [activeBusiness?.id]);

  const loadSavedReports = async () => {
    try {
      const response = await apiRequest('GET', `/api/businesses/${activeBusiness?.id}/reports/saved`);
      setSavedReports(await response.json() as ReportConfig[]);
    } catch (error) {
      console.error('Failed to load saved reports:', error);
    }
  };

  const generateReport = async () => {
    if (!activeBusiness?.id) return;
    
    setIsGenerating(true);
    try {
      // Use real business data for report generation
      const response = await apiRequest('POST', `/api/businesses/${activeBusiness.id}/reports/generate`, {
        config
      });
      setReportData(await response.json());
      toast({
        title: "Report Generated Successfully",
        description: "Your financial report has been created with real business data!",
      });
    } catch (error: any) {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate report",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const saveReport = async () => {
    if (!activeBusiness?.id || !config.name.trim()) {
      toast({
        title: "Save Failed",
        description: "Please enter a report name before saving",
        variant: "destructive",
      });
      return;
    }

    try {
      await apiRequest('POST', `/api/businesses/${activeBusiness.id}/reports/save`, {
        config
      });
      toast({
        title: "Report Saved",
        description: "Your report configuration has been saved successfully!",
      });
      loadSavedReports();
    } catch (error: any) {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save report",
        variant: "destructive",
      });
    }
  };

  const exportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    if (!reportData) {
      toast({
        title: "Export Failed",
        description: "Please generate a report first",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiRequest('POST', `/api/businesses/${activeBusiness?.id}/reports/export`, {
        config,
        data: reportData,
        format
      });
      
      // Create download link for the exported file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.name || 'financial-report'}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Export Complete",
        description: `Financial report exported as ${format.toUpperCase()} successfully!`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: error.message || "Failed to export report",
        variant: "destructive",
      });
    }
  };

  const loadSavedReport = (savedReport: ReportConfig) => {
    setConfig(savedReport);
    toast({
      title: "Report Configuration Loaded",
      description: `Successfully loaded "${savedReport.name}" configuration`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Advanced Report Builder</h2>
          <p className="text-muted-foreground mt-1">Create professional financial reports with real-time data and advanced analytics</p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-2 px-4 py-2">
          <Sparkles className="h-4 w-4" />
          AI-Powered Analytics
        </Badge>
      </div>

      <Tabs defaultValue="builder" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="builder" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Report Builder
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Saved Reports ({savedReports.length})
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview & Export
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Configuration */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-blue-600" />
                  Report Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="report-name">Report Name</Label>
                  <Input
                    id="report-name"
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    placeholder="e.g., Monthly P&L Analysis"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Report Type</Label>
                  <Select value={config.type} onValueChange={(value: any) => setConfig({ ...config, type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Visualization Style</Label>
                  <Select value={config.visualization} onValueChange={(value: any) => setConfig({ ...config, visualization: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISUALIZATION_TYPES.map((viz) => (
                        <SelectItem key={viz.value} value={viz.value}>
                          <div className="flex items-center gap-2">
                            <viz.icon className="h-4 w-4" />
                            {viz.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Date Range & Advanced Options */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-green-600" />
                  Date Range & Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={format(config.dateRange.start, 'yyyy-MM-dd')}
                      onChange={(e) => setConfig({
                        ...config,
                        dateRange: { ...config.dateRange, start: new Date(e.target.value) }
                      })}
                      className="border-gray-300 focus:border-green-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={format(config.dateRange.end, 'yyyy-MM-dd')}
                      onChange={(e) => setConfig({
                        ...config,
                        dateRange: { ...config.dateRange, end: new Date(e.target.value) }
                      })}
                      className="border-gray-300 focus:border-green-500"
                    />
                  </div>
                </div>

                {config.type === 'custom' && (
                  <div className="space-y-2">
                    <Label>Group Data By</Label>
                    <Select value={config.groupBy} onValueChange={(value: any) => setConfig({ ...config, groupBy: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select grouping method..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Monthly Analysis</SelectItem>
                        <SelectItem value="quarter">Quarterly Review</SelectItem>
                        <SelectItem value="year">Annual Summary</SelectItem>
                        <SelectItem value="category">Category Breakdown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="include-sub"
                      checked={config.includeSubAccounts}
                      onChange={(e) => setConfig({ ...config, includeSubAccounts: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="include-sub">Include Sub-Accounts in Analysis</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="compare-previous"
                      checked={config.compareWithPrevious}
                      onChange={(e) => setConfig({ ...config, compareWithPrevious: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="compare-previous">Compare with Previous Period</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-4">
            <Button 
              onClick={generateReport} 
              disabled={isGenerating} 
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Generating Report...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={saveReport} 
              className="flex items-center gap-2 border-blue-600 text-blue-600 hover:bg-blue-50"
              size="lg"
            >
              <Save className="h-4 w-4" />
              Save Configuration
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="saved" className="space-y-4">
          {savedReports.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Save className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Saved Reports Yet</h3>
                <p className="text-muted-foreground mb-4">Create and save your first custom report to see it here</p>
                <Button 
                  variant="outline" 
                  onClick={() => document.querySelector<HTMLElement>('[value="builder"]')?.click()}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Create Your First Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedReports.map((report, index) => (
                <Card key={index} className="cursor-pointer hover:shadow-lg transition-all duration-200 border-gray-200 hover:border-blue-300">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{report.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {report.type.replace('-', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 capitalize">
                      {report.visualization.replace('-', ' ')} visualization
                    </p>
                    <p className="text-xs text-muted-foreground mb-4">
                      {format(report.dateRange.start, 'MMM dd')} - {format(report.dateRange.end, 'MMM dd, yyyy')}
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                      onClick={() => loadSavedReport(report)}
                    >
                      Load Configuration
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          {reportData ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">{config.name || 'Generated Financial Report'}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {format(config.dateRange.start, 'MMM dd, yyyy')} - {format(config.dateRange.end, 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => exportReport('pdf')} className="text-red-600 border-red-200 hover:bg-red-50">
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportReport('excel')} className="text-green-600 border-green-200 hover:bg-green-50">
                      <Download className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportReport('csv')} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 p-6 rounded-lg border">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Professional Financial Report Ready
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Your {config.visualization.replace('-', ' ')} report has been generated with real business data
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm">
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                        <p className="font-medium">Report Type</p>
                        <p className="text-muted-foreground">{config.type.replace('-', ' ').toUpperCase()}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                        <p className="font-medium">Date Range</p>
                        <p className="text-muted-foreground">{format(config.dateRange.start, 'MMM dd')} - {format(config.dateRange.end, 'MMM dd')}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                        <p className="font-medium">Visualization</p>
                        <p className="text-muted-foreground">{config.visualization.replace('-', ' ').toUpperCase()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Report Generated Yet</h3>
                <p className="text-muted-foreground mb-4">Generate a report to see the preview and export options</p>
                <Button 
                  variant="outline" 
                  onClick={() => document.querySelector<HTMLElement>('[value="builder"]')?.click()}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Go to Report Builder
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
